import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  CButton,
  CCol,
  CImage,
  CForm,
  CFormInput,
  CInputGroup,
  CInputGroupText,
  CRow,
  CAlert,
  CFormFeedback,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilLockLocked } from "@coreui/icons";
import { BASE_URL } from "../constant_api";
import bgimage from "../../assets/Images/logo_black.png";
import openEyeIcon from "../../assets/Images/eye-open.png";
import closedEyeIcon from "../../assets/Images/eye-closed.png";

const SetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token");
  const uid = searchParams.get("uid");

  // ── Token verification state ──────────────────────────────────────────
  const [status, setStatus] = useState("checking"); // checking | valid | invalid
  const [checkMessage, setCheckMessage] = useState("");
  const [email, setEmail] = useState("");

  // ── Form state ───────────────────────────────────────────────────────
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [validated, setValidated] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);

  // ── Submission / feedback state ─────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      if (!uid || !token) {
        setStatus("invalid");
        setCheckMessage("This link is missing required information.");
        return;
      }

      try {
        const res = await axios.get(
          `${BASE_URL}/user/verify-set-password-token`,
          { params: { uid, token } },
        );

        if (res.data?.success) {
          setEmail(res.data.data?.EmailAddress || "");
          setStatus("valid");
        } else {
          setStatus("invalid");
          setCheckMessage(res.data?.message || "This link is invalid.");
        }
      } catch (err) {
        setStatus("invalid");
        setCheckMessage(
          err.response?.data?.message ||
            "This link is invalid or has expired.",
        );
      }
    };

    verifyToken();
  }, [uid, token]);

  const togglePasswordVisibility = () => setPasswordVisible((prev) => !prev);
  const toggleConfirmPasswordVisibility = () =>
    setConfirmPasswordVisible((prev) => !prev);

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setShowError(false);
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    setShowError(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    if (form.checkValidity() === false) {
      setValidated(true);
      event.stopPropagation();
      return;
    }

    if (password !== confirmPassword) {
      setShowError(true);
      setErrorMessage("Passwords do not match.");
      return;
    }

    if (
      password.length < 8 ||
      !/[A-Za-z]/.test(password) ||
      !/[0-9]/.test(password)
    ) {
      setShowError(true);
      setErrorMessage(
        "Your password needs at least 8 characters and must contain letters and numbers.",
      );
      return;
    }

    setSubmitting(true);
    setShowError(false);

    try {
      const res = await axios.post(`${BASE_URL}/user/set-password`, {
        uid,
        token,
        password,
        confirmPassword,
      });

      if (res.data?.success) {
        setShowSuccess(true);
        setPassword("");
        setConfirmPassword("");
        setValidated(false);
        setTimeout(() => navigate("/login"), 2000);
      } else {
        setShowError(true);
        setErrorMessage(res.data?.message || "Unable to set password.");
      }
    } catch (err) {
      setShowError(true);
      setErrorMessage(
        err.response?.data?.message ||
          "Something went wrong. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const passwordEyeIcon = passwordVisible ? openEyeIcon : closedEyeIcon;
  const confirmPasswordEyeIcon = confirmPasswordVisible
    ? openEyeIcon
    : closedEyeIcon;
  const errorPosition = { position: "absolute", lineHeight: "10px" };

  return (
    <div className="bg-light min-vh-100 d-flex flex-row align-items-center">
      <div className="lgnauth">
        <div className="lgnform">
          <CRow className="justify-content-center">
            <CCol md={12}>
              {status === "checking" && (
                <h2 className="lgntxt">Checking your link…</h2>
              )}

              {status === "invalid" && (
                <>
                  <h2 className="lgntxt">Link Invalid</h2>
                  <CAlert color="warning">{checkMessage}</CAlert>
                  <p style={{ fontSize: "14px", color: "#1e2433" }}>
                    Please contact your administrator for a new invitation
                    link.
                  </p>
                </>
              )}

              {status === "valid" && !showSuccess && (
                <CForm
                  className="row g-3 needs-validation"
                  noValidate
                  validated={validated}
                  onSubmit={handleSubmit}
                >
                  <h2 className="lgntxt">Set Your Password</h2>

                  {email && (
                    <p style={{ fontSize: "14px", color: "#1e2433", marginTop: 0,textAlign: "center" }}>
                      for <strong>{email}</strong>
                    </p>
                  )}

                  {/* ── Error alert ── */}
                  {showError && (
                    <CAlert
                      color="warning"
                      dismissible
                      onClose={() => setShowError(false)}
                    >
                      {errorMessage}
                    </CAlert>
                  )}

                  {/* ── New Password ── */}
                  <CInputGroup className="mb-2">
                    <div className="single-input-field">
                      <CInputGroupText>
                        <CIcon icon={cilLockLocked} />
                      </CInputGroupText>
                      <CFormInput
                        id="Password"
                        type={passwordVisible ? "text" : "password"}
                        placeholder="Enter a new password"
                        value={password}
                        onChange={handlePasswordChange}
                        required
                        invalid={validated && !password}
                      />
                      <CInputGroupText
                        className="imgpass-2"
                        onClick={togglePasswordVisibility}
                        style={{ cursor: "pointer" }}
                      >
                        <CImage
                          src={passwordEyeIcon}
                          className="eyeimge"
                          style={{ width: "20px" }}
                        />
                      </CInputGroupText>
                      <CFormFeedback invalid style={errorPosition}>
                        Please enter a new password.
                      </CFormFeedback>
                    </div>
                  </CInputGroup>

                  {/* ── Confirm Password ── */}
                  <CInputGroup className="mb-3">
                    <div className="single-input-field">
                      <CInputGroupText>
                        <CIcon icon={cilLockLocked} />
                      </CInputGroupText>
                      <CFormInput
                        id="ConfirmPassword"
                        type={confirmPasswordVisible ? "text" : "password"}
                        placeholder="Re-enter your password"
                        value={confirmPassword}
                        onChange={handleConfirmPasswordChange}
                        required
                        invalid={validated && !confirmPassword}
                      />
                      <CInputGroupText
                        className="imgpass-2"
                        onClick={toggleConfirmPasswordVisibility}
                        style={{ cursor: "pointer" }}
                      >
                        <CImage
                          src={confirmPasswordEyeIcon}
                          className="eyeimge"
                          style={{ width: "20px" }}
                        />
                      </CInputGroupText>
                      <CFormFeedback invalid style={errorPosition}>
                        Please confirm your password.
                      </CFormFeedback>
                    </div>
                  </CInputGroup>

                  {/* ── Submit ── */}
                  <CButton
                    color="primary"
                    className="px-4 btnsign"
                    type="submit"
                    disabled={submitting}
                  >
                    {submitting ? "Please wait..." : "Set Password"}
                  </CButton>
                </CForm>
              )}

              {showSuccess && (
                <>
                  <h2 className="lgntxt">Password Set 🎉</h2>
                  <CAlert color="success">
                    Your password has been set successfully. Redirecting you
                    to login…
                  </CAlert>
                </>
              )}
            </CCol>
          </CRow>
        </div>

        {/* ── Logo / welcome panel ── */}
        <div className="logobg">
          <h2 className="weltxt">Welcome To</h2>
          <CImage
            src={bgimage}
            height="auto"
            align="center"
            width={300}
            className="mainlgo"
          />
          <p className="qqtxt">
            Lorem Ipsum is simply dummy text of the printing and typesetting
            industry. Lorem Ipsum has been the industry's standard dummy text
            ever since the 1500s, when an unknown printer took a galley of
            type and scrambled it to make a type specimen book.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SetPassword;